import { AfterViewInit, Component, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { ConnectAppsDockComponent } from '../connect-apps/connect-apps-dock.component';

export interface NavItem {
    label: string;
    icon: string;
    /** id of a section on the login page - reached via /login?scrollTo=<section> */
    section?: string;
    /** a route of its own, for cross-page links */
    route?: string;
    /** extra tint class, matching the old highlighted pills */
    accent?: string;
    children?: NavItem[];
}

export interface NavGroup {
    key: string;
    label: string;
    items: NavItem[];
}

/**
 * The public-site navigation, shared by the story, login, developers and
 * recipes pages. It replaces the fixed top bar those four templates each
 * carried their own copy of.
 *
 * Everything floats and nothing reserves page width:
 *
 *  - A pill fixed at the top of every page holds the destinations - the brand,
 *    the three sibling pages and Sign In.
 *
 *  - Pages with in-page sections (showSections - the login page, today) get a
 *    segmented track of those sections in the middle of that pill, with the
 *    one you are in filled. They stay visible rather than living behind a
 *    dropdown: most visitors here are deciding whether the product is worth
 *    their time, and they will not open a menu to find that out.
 *
 * Below COMPACT_WIDTH the pill has no room for ten items, so it shrinks to
 * brand + Sign In and both lists move into a hamburger drawer. The old top bar
 * was hidden outright at that width, leaving mobile with no navigation.
 *
 * Section order follows the order the sections appear on the page: demo,
 * architecture, features, connectors, examples.
 */
@Component({
    selector: 'app-site-nav',
    standalone: true,
    imports: [CommonModule, RouterModule, ConnectAppsDockComponent],
    templateUrl: './site-nav.component.html',
    styleUrls: ['./site-nav.component.css']
})
export class SiteNavComponent implements OnInit, AfterViewInit, OnDestroy {

    /** Does this page have in-page sections worth a menu? Login only, today. */
    @Input() showSections = false;

    /** The "Your apps" dock. Off on the landing page, whose first screen is the same three apps. */
    @Input() showDock = true;

    /**
     * Labels are the customer's words, not the codebase's: these chips are the
     * first thing a non-technical visitor reads, and "Architecture" and
     * "Connectors" tell them nothing about what they get. Both replacements are
     * lifted from headings already on the sections themselves, and both fit the
     * track at the width where it is tightest - a longer phrase would push the
     * breakpoint past most laptops and hide the chips behind a hamburger, which
     * defeats showing them at all. The scrollTo ids are untouched, so existing
     * /login?scrollTo= links keep working.
     */
    readonly sectionGroup: NavGroup = {
        key: 'sections',
        label: 'On this page',
        items: [
            { label: 'Demo', icon: 'fa-solid fa-circle-play', section: 'demo' },
            { label: 'How it works', icon: 'fa-solid fa-layer-group', section: 'architecture' },
            {
                label: 'Features', icon: 'fa-solid fa-table-cells-large', section: 'features',
                children: [
                    { label: 'Memories', icon: 'fa-regular fa-lightbulb', section: 'feature-memories' },
                    { label: 'Images', icon: 'fa-regular fa-image', section: 'feature-images' },
                    { label: 'Search', icon: 'fa-solid fa-magnifying-glass', section: 'feature-search' },
                    { label: 'Reminders', icon: 'fa-regular fa-bell', section: 'feature-reminders' }
                ]
            },
            { label: 'Your apps', icon: 'fa-solid fa-plug', section: 'tutorial' },
            { label: 'Examples', icon: 'fa-regular fa-star', section: 'examples' }
        ]
    };

    readonly pageGroup: NavGroup = {
        key: 'pages',
        label: 'Pages',
        items: [
            { label: 'Our Story', icon: 'fa-regular fa-clock', route: '/story', accent: 'story' },
            { label: 'Recipes', icon: 'fa-regular fa-file-lines', route: '/recipes', accent: 'recipes' },
            { label: 'Developers', icon: 'fa-solid fa-code', route: '/developers', accent: 'developers' }
        ]
    };

    /** What the mobile drawer shows - sections first, then the destinations. */
    drawerGroups: NavGroup[] = [];

    /** Which drawer groups are open. Both start open - collapsing is opt-in. */
    openGroups: Record<string, boolean> = { sections: true, pages: true };

    /** Which parent items have their children showing. */
    openItems: Record<string, boolean> = {};

    activeSection = '';
    /** Label of the section chip whose sub-menu popover is open, if any. */
    openSubMenu: string | null = null;
    drawerOpen = false;
    isCompact = false;

    /**
     * Below this the pill cannot hold the brand, five section chips, three page
     * links and Sign In at once, so everything but the brand and Sign In moves
     * into the drawer.
     */
    private readonly COMPACT_WIDTH = 1180;

    /** Clears the floating pill, which the login page scrolls underneath. */
    private readonly SCROLL_OFFSET = 80;
    private spyObserver?: IntersectionObserver;
    private routerSub?: Subscription;

    constructor(
        private router: Router,
        private ngZone: NgZone,
        private host: ElementRef<HTMLElement>
    ) { }

    ngOnInit(): void {
        this.onResize();

        // Only fires for navigations that reuse this instance - a ?scrollTo=
        // change on the page we are already on. Arriving at a new page is
        // handled by ngAfterViewInit instead; see the note there.
        this.routerSub = this.router.events
            .pipe(filter(e => e instanceof NavigationEnd))
            .subscribe(() => {
                this.drawerOpen = false;
                this.openSubMenu = null;
            });
    }

    /**
     * A fresh instance of this component means a fresh page, so this is where
     * arriving somewhere new is handled - not in a NavigationEnd subscription.
     *
     * Neither instance can see the NavigationEnd for its own arrival: the
     * outgoing page's nav is destroyed during route activation, and the
     * incoming one is a child of the routed component, so it is not
     * constructed until the change detection pass that follows the event.
     * Subscribing from either end catches nothing.
     */
    ngAfterViewInit(): void {
        this.resetScroll();
        // The sibling sections exist by now, but images and *ngIf blocks above
        // them are still settling, so give their offsets a moment.
        setTimeout(() => this.setupScrollSpy(), 250);
    }

    ngOnDestroy(): void {
        this.spyObserver?.disconnect();
        this.routerSub?.unsubscribe();
    }

    @HostListener('window:resize')
    onResize(): void {
        this.isCompact = window.innerWidth <= this.COMPACT_WIDTH;
        if (this.isCompact) {
            this.openSubMenu = null;
        } else {
            this.drawerOpen = false;
        }
        this.drawerGroups = this.showSections
            ? [this.sectionGroup, this.pageGroup]
            : [this.pageGroup];
    }

    /** Clicking anywhere outside an open sub-menu popover dismisses it. */
    @HostListener('document:click', ['$event'])
    onDocumentClick(ev: MouseEvent): void {
        if (!this.openSubMenu) return;
        const open = this.host.nativeElement.querySelector('.chip-wrap.has-open-menu');
        if (open && !open.contains(ev.target as Node)) this.openSubMenu = null;
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.openSubMenu = null;
        this.drawerOpen = false;
    }

    get isLoginPage(): boolean {
        return this.router.url.split('?')[0] === '/login';
    }

    /**
     * The ?scrollTo= of the URL being navigated to, read off the router rather
     * than the injected ActivatedRoute.
     *
     * Nested components render during the router outlet's first change
     * detection, which happens *after* NavigationEnd - so the instance that
     * actually receives NavigationEnd is the outgoing page's, whose route
     * snapshot still carries the old page's query params. Reading router.url
     * gives whoever handles the event the incoming URL either way.
     */
    private get scrollTarget(): string | null {
        const query = this.router.url.split('?')[1];
        if (!query) return null;
        return new URLSearchParams(query).get('scrollTo');
    }

    /** Opens the sub-menu hanging off a section chip, e.g. Features. */
    toggleSubMenu(item: NavItem, ev: Event): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.openSubMenu = this.openSubMenu === item.label ? null : item.label;
    }

    toggleGroup(key: string): void {
        this.openGroups[key] = !this.openGroups[key];
    }

    toggleItem(item: NavItem, ev: Event): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.openItems[item.label] = !this.openItems[item.label];
    }

    isItemOpen(item: NavItem): boolean {
        // Auto-expand the parent whose child is currently in view, so the
        // accordion follows the reader instead of having to be driven.
        if (item.children?.some(c => c.section === this.activeSection)) return true;
        return !!this.openItems[item.label];
    }

    isActive(item: NavItem): boolean {
        if (item.section === this.activeSection) return true;
        return !!item.children?.some(c => c.section === this.activeSection);
    }

    /**
     * Clicking a section link normally navigates to /login?scrollTo=<id> and the
     * login page's queryParams subscription does the scrolling. When the param
     * is already set to that id the router treats the click as a no-op, so
     * scroll here instead - otherwise the second click on a link does nothing.
     */
    onSectionClick(item: NavItem): void {
        this.openSubMenu = null;
        this.drawerOpen = false;
        if (this.isLoginPage && this.scrollTarget === item.section) {
            this.scrollToSection(item.section!);
        }
    }

    closeDrawer(): void {
        this.drawerOpen = false;
    }

    toggleDrawer(): void {
        this.drawerOpen = !this.drawerOpen;
    }

    /**
     * These pages share one <mat-sidenav-content> scroller, which keeps its
     * scrollTop across route changes - so following a link from the bottom of
     * the login page used to drop you at the bottom of the recipes page.
     * Angular's scrollPositionRestoration can't help: it drives the window,
     * which is not what scrolls here.
     *
     * It runs twice: once now, and once after the incoming view has rendered.
     * The first pass alone lands while the old view is being torn down, and
     * Chrome's scroll anchoring pulls the offset straight back as the new
     * content fills in.
     */
    private resetScroll(): void {
        if (this.scrollTarget) return;
        const toTop = () => {
            const scroller = document.querySelector('.mat-sidenav-content') as HTMLElement | null;
            if (scroller) scroller.scrollTop = 0;
            window.scrollTo(0, 0);
        };
        toTop();
        setTimeout(toTop);
    }

    private setupScrollSpy(): void {
        this.spyObserver?.disconnect();
        // Sections live on the login page only. Without this reset the chip
        // filled when you left it stays filled on every other page.
        this.activeSection = '';

        const ids = this.sectionGroup.items
            .flatMap(i => [i, ...(i.children ?? [])])
            .map(i => i.section)
            .filter((id): id is string => !!id);

        this.spyObserver = new IntersectionObserver((entries) => {
            this.ngZone.run(() => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        this.activeSection = (entry.target as HTMLElement).id;
                    }
                }
            });
        }, { rootMargin: '-90px 0px -65% 0px', threshold: 0 });

        for (const id of ids) {
            const el = document.getElementById(id);
            if (el) this.spyObserver.observe(el);
        }
    }

    /**
     * The login page scrolls inside <mat-sidenav-content>, not the window, and
     * that container reports clientHeight 0 - which makes scrollIntoView() and
     * scrollTo({behavior:'smooth'}) both no-ops on it. Assigning scrollTop is
     * the one thing that works, so the easing is hand-rolled.
     */
    private scrollToSection(sectionId: string): void {
        const element = document.getElementById(sectionId);
        if (!element) return;

        this.ngZone.runOutsideAngular(() => {
            const scroller = element.closest('.mat-sidenav-content') as HTMLElement | null;
            if (scroller) {
                const top = element.getBoundingClientRect().top + scroller.scrollTop - this.SCROLL_OFFSET;
                this.animateScroll(scroller, Math.max(0, top));
            } else {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    }

    private animateScroll(scroller: HTMLElement, top: number): void {
        const start = scroller.scrollTop;
        const distance = top - start;
        if (Math.abs(distance) < 2) return;

        // requestAnimationFrame is suspended while the page is hidden, which
        // would leave the scroll half-done on a background tab.
        if (document.hidden) {
            scroller.scrollTop = top;
            return;
        }

        const duration = 500;
        let startedAt: number | null = null;

        const step = (now: number) => {
            if (startedAt === null) startedAt = now;
            const t = Math.min(1, (now - startedAt) / duration);
            const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            scroller.scrollTop = start + distance * eased;
            if (t < 1) requestAnimationFrame(step);
        };

        requestAnimationFrame(step);
    }
}
